import axios from 'axios';
import { API_CONFIG } from './constants';

/**
 * Service API pour l'inscription aux activités
 */
class ActivityRegistrationService {
  constructor() {
    this.token = process.env.REACT_APP_M2M_TOKEN_FOR_SERVER;
    this.baseURL = API_CONFIG.baseURL;
  }

  /**
   * Génère les headers pour les requêtes API
   * @returns {Object} Headers avec authentification
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    
    return headers;
  }

  /**
   * Récupère les activités depuis l'API
   * @returns {Promise<Array>} Liste des activités
   */
  async fetchActivities() {
    try {
      const response = await axios.get(
        `${this.baseURL}${API_CONFIG.endpoints.activities}?category=Salon/Conférence`,
        { headers: this.getHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des activités:', error);
      throw new Error(
        error.response?.data?.message || 
        'Erreur lors de la récupération des activités'
      );
    }
  }

  /**
   * Recherche un lead par email
   * @param {string} email - L'email à rechercher
   * @returns {Promise<Object|null>} Le lead trouvé ou null
   */
  async searchLeadByEmail(email) {
    try {
      const filters = {
        included: {
          email: [email]  // ✅ Correct field name selon l'API
        },
        fieldsToSend: [
          'ID',           // ✅ Sans préfixe dans la requête
          'firstName',    // ✅ Sans préfixe dans la requête
          'lastName',     // ✅ Sans préfixe dans la requête
          'phoneNumber'   // ✅ Sans préfixe dans la requête
        ]
      };

      const response = await axios.post(
        `${this.baseURL}${API_CONFIG.endpoints.leadFilters}`,
        filters,
        { headers: this.getHeaders() }
      );

      return response.data && response.data.length > 0 ? response.data[0] : null;
    } catch (error) {
      console.error('❌ Erreur lors de la recherche du lead:', error);
      console.error('📝 Détails de l\'erreur:', error.response?.data);
      throw new Error(
        error.response?.data?.message || 
        'Erreur lors de la recherche du lead'
      );
    }
  }

  /**
   * Soumet un participant à l'activité
   * @param {Object} participantData - Les données du participant
   * @returns {Promise<Object>} La réponse de l'API
   */
  async submitParticipant(participantData) {
    try {
      const response = await axios.post(
        `${this.baseURL}${API_CONFIG.endpoints.activityConf}`,
        participantData,
        { headers: this.getHeaders() }
      );
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la soumission du participant:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erreur lors de l\'inscription du participant'
      );
    }
  }

  /**
   * Soumet tous les participants en parallèle
   * @param {Array} participants - Liste des participants
   * @param {string} selectedActivity - ID de l'activité sélectionnée
   * @returns {Promise<Array>} Résultats des soumissions
   */
  async submitAllParticipants(participants, selectedActivity, prepareParticipantData) {
    const promises = participants.map(async (participant) => {
      try {
        const participantData = prepareParticipantData(participant, selectedActivity);
        const response = await this.submitParticipant(participantData);
        
        return {
          participant,
          success: true,
          data: response,
          error: null
        };
      } catch (error) {
        console.error('Erreur pour participant:', participant, error);
        return {
          participant,
          success: false,
          data: null,
          error: error.message
        };
      }
    });

    return Promise.all(promises);
  }
}

// Export d'une instance du service
export const activityRegistrationService = new ActivityRegistrationService();
export default activityRegistrationService;